import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import axios from "axios";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { COUNTRY_CODES, formatName, formatPhone, validateNamePhone } from "@/lib/countryCodes";
import {
  Phone, Mail, MapPin, Clock, MessageCircle, ArrowRight, Upload, CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const treatmentOptions = [
  "Cardiology", "Orthopedics", "Oncology", "Neurology", "Fertility",
  "Cosmetic Surgery", "Dental", "Eye Care", "Transplant", "Other"
];

export default function ContactPage() {
  const { t } = useLanguage();
  const [formType, setFormType] = useState("inquiry");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [inquiry, setInquiry] = useState({
    name: "", email: "", countryCode: "IN|+91", phoneNumber: "", country: "", treatment: "", message: ""
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const [contact, setContact] = useState({ name: "", email: "", countryCode: "IN|+91", phoneNumber: "", message: "" });

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    const errs = validateNamePhone(inquiry.name, inquiry.phoneNumber);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(Object.values(errs)[0]);
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", inquiry.name.trim());
      formData.append("email", inquiry.email);
      formData.append("phone", `${inquiry.countryCode.split("|")[1]} ${inquiry.phoneNumber}`);
      formData.append("country", inquiry.country);
      formData.append("treatment", inquiry.treatment);
      formData.append("message", inquiry.message);
      if (file) formData.append("report", file);
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Inquiry submitted successfully! We'll get back to you within 48 hours.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const errs = validateNamePhone(contact.name, contact.phoneNumber);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(Object.values(errs)[0]);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: contact.name.trim(),
        email: contact.email,
        phone: `${contact.countryCode.split("|")[1]} ${contact.phoneNumber}`,
        message: contact.message
      });
      toast.success("Message sent successfully!");
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-page" className="section-padding bg-[#F9F9F8]">
        <div className="max-w-xl mx-auto text-center">
          <FadeIn>
            <div className="w-16 h-16 rounded-full bg-[#e0f2fe] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#00A4A9]" />
            </div>
            <h2 className="font-outfit text-3xl font-medium text-[#03045e] mb-4">{t("contact.thankYou")}</h2>
            <p className="text-slate-600 mb-8">{t("contact.thankYouMsg")}</p>
            <Button
              onClick={() => { setSubmitted(false); setErrors({}); setInquiry({ name: "", email: "", countryCode: "IN|+91", phoneNumber: "", country: "", treatment: "", message: "" }); setFile(null); setContact({ name: "", email: "", countryCode: "IN|+91", phoneNumber: "", message: "" }); }}
              className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white"
              data-testid="submit-another-btn"
            >
              {t("contact.submitAnother")}
            </Button>
          </FadeIn>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <SEO title="Contact Us" description="Get a free medical consultation. Submit your inquiry or upload medical reports. 24/7 patient support via WhatsApp, email, and phone." path="/contact" keywords="contact medical tourism India, free medical consultation, medical report upload, patient inquiry" />
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <FadeIn>
                <span className="overline">{t("contact.getInTouch")}</span>
                <h1 className="font-outfit text-4xl sm:text-5xl font-light text-[#03045e] mt-3 mb-6 tracking-tight">
                  {t("contact.title")} <span className="font-medium text-[#00A4A9]">{t("contact.titleHighlight")}</span>
                </h1>

                <div className="flex gap-2 mb-8">
                  <Button
                    variant={formType === "inquiry" ? "default" : "outline"}
                    className={`rounded-full text-sm ${formType === "inquiry" ? "bg-[#00A4A9] hover:bg-[#008084]" : ""}`}
                    onClick={() => setFormType("inquiry")}
                    data-testid="tab-inquiry"
                  >
                    {t("contact.inquiry")}
                  </Button>
                  <Button
                    variant={formType === "contact" ? "default" : "outline"}
                    className={`rounded-full text-sm ${formType === "contact" ? "bg-[#00A4A9] hover:bg-[#008084]" : ""}`}
                    onClick={() => setFormType("contact")}
                    data-testid="tab-contact"
                  >
                    {t("contact.general")}
                  </Button>
                </div>

                {formType === "inquiry" ? (
                  <form onSubmit={handleInquirySubmit} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="inquiry-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                      <div>
                        <Label htmlFor="inq-name" className="text-sm text-slate-700 mb-1.5 block">{t("contact.name")} *</Label>
                        <Input id="inq-name" required value={inquiry.name} onChange={(e) => setInquiry({ ...inquiry, name: formatName(e.target.value) })} className="rounded-lg border-slate-300 focus:ring-[#00A4A9]" data-testid="inquiry-name" placeholder="John Doe" />
                        {errors.name && <p className="text-xs text-red-500 mt-1" data-testid="inquiry-name-error">{errors.name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="inq-email" className="text-sm text-slate-700 mb-1.5 block">{t("contact.email")} *</Label>
                        <Input id="inq-email" type="email" required value={inquiry.email} onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })} className="rounded-lg border-slate-300 focus:ring-[#00A4A9]" data-testid="inquiry-email" placeholder="john@example.com" />
                      </div>
                      <div>
                        <Label htmlFor="inq-phone" className="text-sm text-slate-700 mb-1.5 block">{t("contact.phone")} *</Label>
                        <div className="flex gap-2">
                          <select
                            required
                            value={inquiry.countryCode}
                            onChange={(e) => setInquiry({ ...inquiry, countryCode: e.target.value })}
                            className="h-10 w-28 flex-shrink-0 rounded-lg border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A4A9] bg-white"
                            data-testid="inquiry-country-code"
                          >
                            {COUNTRY_CODES.map((c) => (
                              <option key={c.code} value={`${c.code}|${c.dial}`}>{c.dial} {c.name}</option>
                            ))}
                          </select>
                          <Input id="inq-phone" required inputMode="numeric" value={inquiry.phoneNumber} onChange={(e) => setInquiry({ ...inquiry, phoneNumber: formatPhone(e.target.value) })} className="rounded-lg border-slate-300 focus:ring-[#00A4A9]" data-testid="inquiry-phone" placeholder="9876543210" />
                        </div>
                        {errors.phone && <p className="text-xs text-red-500 mt-1" data-testid="inquiry-phone-error">{errors.phone}</p>}
                      </div>
                      <div>
                        <Label htmlFor="inq-country" className="text-sm text-slate-700 mb-1.5 block">{t("contact.country")} *</Label>
                        <select
                          id="inq-country"
                          required
                          value={inquiry.country}
                          onChange={(e) => setInquiry({ ...inquiry, country: e.target.value })}
                          className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A4A9] focus:border-[#00A4A9] bg-white"
                          data-testid="inquiry-country"
                        >
                          <option value="">Select your country</option>
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.code} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-5">
                      <Label htmlFor="inq-treatment" className="text-sm text-slate-700 mb-1.5 block">{t("contact.treatment")} *</Label>
                      <select
                        id="inq-treatment"
                        required
                        value={inquiry.treatment}
                        onChange={(e) => setInquiry({ ...inquiry, treatment: e.target.value })}
                        className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A4A9] focus:border-[#00A4A9] bg-white"
                        data-testid="inquiry-treatment"
                      >
                        <option value="">{t("contact.selectTreatment")}</option>
                        {treatmentOptions.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-5">
                      <Label htmlFor="inq-message" className="text-sm text-slate-700 mb-1.5 block">{t("contact.message")}</Label>
                      <Textarea id="inq-message" value={inquiry.message} onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })} className="rounded-lg border-slate-300 focus:ring-[#00A4A9] min-h-[100px]" data-testid="inquiry-message" placeholder={t("contact.messagePlaceholder")} />
                    </div>
                    <div className="mb-6">
                      <Label className="text-sm text-slate-700 mb-1.5 block">{t("contact.upload")}</Label>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-[#00A4A9] transition-colors cursor-pointer" onClick={() => document.getElementById("file-upload").click()}>
                        <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">{file ? file.name : t("contact.uploadClick")}</p>
                        <p className="text-xs text-slate-400 mt-1">{t("contact.uploadHint")}</p>
                        <input id="file-upload" type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} data-testid="inquiry-file-upload" />
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white h-12 btn-hover" data-testid="inquiry-submit-btn">
                      {loading ? t("contact.submitting") : t("contact.submit")}
                      {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleContactSubmit} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                      <div>
                        <Label htmlFor="ct-name" className="text-sm text-slate-700 mb-1.5 block">{t("contact.name")} *</Label>
                        <Input id="ct-name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: formatName(e.target.value) })} className="rounded-lg border-slate-300" data-testid="contact-name" placeholder="John Doe" />
                        {errors.name && <p className="text-xs text-red-500 mt-1" data-testid="contact-name-error">{errors.name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="ct-email" className="text-sm text-slate-700 mb-1.5 block">{t("contact.email")} *</Label>
                        <Input id="ct-email" type="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="rounded-lg border-slate-300" data-testid="contact-email" placeholder="john@example.com" />
                      </div>
                    </div>
                    <div className="mb-5">
                      <Label htmlFor="ct-phone" className="text-sm text-slate-700 mb-1.5 block">{t("contact.phone")} *</Label>
                      <div className="flex gap-2">
                        <select
                          required
                          value={contact.countryCode}
                          onChange={(e) => setContact({ ...contact, countryCode: e.target.value })}
                          className="h-10 w-28 flex-shrink-0 rounded-lg border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A4A9] bg-white"
                          data-testid="contact-country-code"
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.code} value={`${c.code}|${c.dial}`}>{c.dial} {c.name}</option>
                          ))}
                        </select>
                        <Input id="ct-phone" required inputMode="numeric" value={contact.phoneNumber} onChange={(e) => setContact({ ...contact, phoneNumber: formatPhone(e.target.value) })} className="rounded-lg border-slate-300" data-testid="contact-phone" placeholder="9876543210" />
                      </div>
                      {errors.phone && <p className="text-xs text-red-500 mt-1" data-testid="contact-phone-error">{errors.phone}</p>}
                    </div>
                    <div className="mb-6">
                      <Label htmlFor="ct-message" className="text-sm text-slate-700 mb-1.5 block">{t("contact.message")} *</Label>
                      <Textarea id="ct-message" required value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className="rounded-lg border-slate-300 min-h-[120px]" data-testid="contact-message" placeholder={t("contact.howCanWeHelp")} />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white h-12 btn-hover" data-testid="contact-submit-btn">
                      {loading ? t("contact.sending") : t("contact.send")}
                    </Button>
                  </form>
                )}
              </FadeIn>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              <FadeIn delay={200}>
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-7 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                    <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-5">{t("contact.contactInfo")}</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#e0f2fe] flex items-center justify-center flex-shrink-0">
                          <Phone className="w-4 h-4 text-[#00A4A9]" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#03045e]">Phone</div>
                          <a href="tel:+918891516363" className="text-sm text-slate-500 hover:text-[#00A4A9]">+91 88915 16363</a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#e0f2fe] flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-[#00A4A9]" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#03045e]">Email</div>
                          <div className="text-sm text-slate-500">info@flarialmeditours.com</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#e0f2fe] flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-4 h-4 text-[#00A4A9]" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#03045e]">WhatsApp</div>
                          <a href={buildWhatsAppUrl("Hi, I'd like to reach out about medical treatment.")} target="_blank" rel="noopener noreferrer" className="text-sm text-[#00A4A9] hover:underline" data-testid="contact-whatsapp-link">
                            Chat with us
                          </a>
                        </div>
                      </div>

                      {/* WhatsApp QR */}
                      <div className="mt-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#e0f2fe]/40 to-white p-5" data-testid="whatsapp-qr-block">
                        <div className="flex items-center gap-4">
                          <img
                            src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=https%3A%2F%2Fwa.me%2F918891516363%3Ftext%3DHi%252C%2520I%2527d%2520like%2520to%2520inquire%2520about%2520medical%2520treatment%2520in%2520Kerala."
                            alt="Scan to chat on WhatsApp"
                            width="120"
                            height="120"
                            className="rounded-lg border border-slate-200 bg-white p-2"
                            data-testid="whatsapp-qr-image"
                          />
                          <div className="flex-1">
                            <div className="text-xs font-semibold tracking-wider text-[#00A4A9] uppercase mb-1">Scan to Chat</div>
                            <div className="font-outfit text-sm font-medium text-[#03045e] mb-1">Open WhatsApp instantly</div>
                            <p className="text-xs text-slate-500 leading-relaxed">Scan with your phone camera — your message will be pre-filled.</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#e0f2fe] flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-[#00A4A9]" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#03045e]">Office</div>
                          <div className="text-sm text-slate-500 leading-relaxed">
                            Flarial Technologies Pvt. Ltd. — Kochi<br />
                            Flarial MediTours<br />
                            Seat No.3, SBC-2, W Room,<br />
                            1st Floor, Thapasya Building,<br />
                            Infopark Kochi P.O,<br />
                            Kakkanad — 682042,<br />
                            Kerala, India
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#e0f2fe] flex items-center justify-center flex-shrink-0">
                          <Clock className="w-4 h-4 text-[#00A4A9]" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#03045e]">Hours</div>
                          <div className="text-sm text-slate-500">24/7 Patient Support</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-7 border border-slate-200">
                    <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-3">{t("contact.dataPrivacy")}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{t("contact.dataPrivacyDesc")}</p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
